<template>
  <span
    class="app-icon"
    :class="{ 'app-icon-pulse': pulse, 'app-icon-missing': !iconDef }"
    :style="iconStyle"
    :title="title"
    aria-hidden="true"
  >
    <img
      v-if="iconDef && iconDef.type === 'image'"
      :src="iconDef.src"
      :alt="title || name"
      class="app-icon-image"
      width="1em"
      height="1em"
      :style="mediaStyle"
    />
    <svg
      v-else-if="iconDef"
      class="app-icon-svg"
      :viewBox="iconDef.viewBox"
      width="1em"
      height="1em"
      :style="mediaStyle"
      xmlns="http://www.w3.org/2000/svg"
      v-html="iconDef.raw"
    />
    <span v-else class="app-icon-fallback"></span>
  </span>
</template>

<script>
import iconThumbsUpDown from '@/assets/icons/icon-thumb-up-down.png';
import iconPercentageScale from '@/assets/icons/icon-percentage-scale.png';
import iconMultiSelect from '@/assets/icons/icon-multi-select.png';
import iconStackRanking from '@/assets/icons/icon-stack-ranking.png';
import iconOpenText from '@/assets/icons/icon-open-text.png';

const iconMap = {
  search: {
    viewBox: '0 0 24 24',
    raw: '<circle cx="10" cy="10" r="6.5" fill="none" stroke="currentColor" stroke-width="2"></circle><path d="M15 15l6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"></path>'
  },
  'check-square-o': {
    viewBox: '0 0 24 24',
    raw: '<rect x="3.5" y="3.5" width="17" height="17" rx="1.5" fill="none" stroke="currentColor" stroke-width="2"></rect><path d="M7.5 12.5l3 3 6-7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>'
  },
  'file-o': {
    viewBox: '0 0 24 24',
    raw: '<path d="M6 2.5h8l4 4v15H6z" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"></path><path d="M14 2.5v5h4" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"></path>'
  },
  'clock-o': {
    viewBox: '0 0 24 24',
    raw: '<circle cx="12" cy="12" r="8.5" fill="none" stroke="currentColor" stroke-width="2"></circle><path d="M12 7.5v5l3 2" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>'
  },
  'list-ul': {
    viewBox: '0 0 24 24',
    raw: '<circle cx="5" cy="6" r="1.5" fill="currentColor"></circle><circle cx="5" cy="12" r="1.5" fill="currentColor"></circle><circle cx="5" cy="18" r="1.5" fill="currentColor"></circle><path d="M9 6h10M9 12h10M9 18h10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"></path>'
  },
  'caret-right': {
    viewBox: '0 0 24 24',
    raw: '<path d="M9 6l8 6-8 6z" fill="currentColor"></path>'
  },
  'angle-down': {
    viewBox: '0 0 24 24',
    raw: '<path d="M6.5 9l5.5 6 5.5-6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>'
  },
  'angle-left': {
    viewBox: '0 0 24 24',
    raw: '<path d="M15 6l-6 6 6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>'
  },
  'angle-right': {
    viewBox: '0 0 24 24',
    raw: '<path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>'
  },
  'align-justify': {
    viewBox: '0 0 24 24',
    raw: '<path d="M4 7h16M4 12h16M4 17h16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"></path>'
  },
  check: {
    viewBox: '0 0 24 24',
    raw: '<path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"></path>'
  },
  spinner: {
    viewBox: '0 0 24 24',
    raw: '<circle cx="12" cy="12" r="8.5" fill="none" stroke="currentColor" stroke-opacity="0.3" stroke-width="2"></circle><path d="M12 3.5a8.5 8.5 0 0 1 8.5 8.5" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"></path>'
  },
  calendar: {
    viewBox: '0 0 24 24',
    raw: '<rect x="3.5" y="5.5" width="17" height="15" rx="1.5" fill="none" stroke="currentColor" stroke-width="2"></rect><path d="M7 3.5v4M17 3.5v4M3.5 9.5h17" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"></path>'
  },
  times: {
    viewBox: '0 0 24 24',
    raw: '<path d="M7 7l10 10M17 7L7 17" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round"></path>'
  },
  'check-circle-o': {
    viewBox: '0 0 24 24',
    raw: '<circle cx="12" cy="12" r="8.5" fill="none" stroke="currentColor" stroke-width="2"></circle><path d="M8.5 12.5l2.5 2.5 4.5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>'
  },
  'pause-circle-o': {
    viewBox: '0 0 24 24',
    raw: '<circle cx="12" cy="12" r="8.5" fill="none" stroke="currentColor" stroke-width="2"></circle><path d="M10 8.5v7M14 8.5v7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"></path>'
  },
  'info-circle': {
    viewBox: '0 0 24 24',
    raw: '<circle cx="12" cy="12" r="8.5" fill="none" stroke="currentColor" stroke-width="2"></circle><path d="M12 10.5v5M12 7.75h.01" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"></path>'
  },
  play: {
    viewBox: '0 0 24 24',
    raw: '<path d="M8 6.5l9 5.5-9 5.5z" fill="currentColor"></path>'
  },
  'thumbs-up-down': {
    type: 'image',
    src: iconThumbsUpDown
  },
  'percentage-scale': {
    type: 'image',
    src: iconPercentageScale
  },
  'multiple-select': {
    type: 'image',
    src: iconMultiSelect
  },
  'stack-ranking': {
    type: 'image',
    src: iconStackRanking
  },
  'open-text': {
    type: 'image',
    src: iconOpenText
  }
};

export default {
  name: 'AppIcon',
  props: {
    name: {
      type: String,
      required: true
    },
    scale: {
      type: [String, Number],
      default: 1
    },
    pulse: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ''
    }
  },
  computed: {
    iconDef () {
      return iconMap[this.name] || null;
    },
    iconStyle () {
      const scale = Number(this.scale) || 1;
      return scale === 1 ? null : {
        fontSize: `${scale}em`
      };
    },
    mediaStyle () {
      return {
        width: '1em',
        height: '1em',
        maxWidth: '100%'
      };
    }
  }
};
</script>

<style scoped>
.app-icon {
  display: inline-block;
  width: 1em;
  height: 1em;
  line-height: 1;
  vertical-align: middle;
  overflow: hidden;
}

.app-icon-svg,
.app-icon-image {
  display: block;
  width: 1em;
  height: 1em;
  max-width: 100%;
}

.app-icon-pulse {
  animation: app-icon-pulse 1s linear infinite;
}

.app-icon-fallback {
  display: inline-block;
  width: 1em;
  height: 1em;
  border-radius: 999px;
  background: currentColor;
  opacity: 0.25;
}

@keyframes app-icon-pulse {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>
